import { useState, useCallback } from 'react';
import axios from 'axios';
import verificationApi from '@/shared/services/verificationApi';
import { useAppStore } from '@/store/useAppStore';
import { useLocation } from "react-router-dom";
import { getUtmQsParams } from '@/shared/utils/utmUtils';

interface VerifyTokenRequest {
  id?: string;
  token: string;
}

interface VerifyTokenResponse {
  success: boolean;
  code: number;
  message: string;
  tempDataId?: string;
  tempData?: {
    tempDataId: string;
    verificationId: number;
    planType: number;
    startTrip: string;
    destinations: string;
    endTrip: string;
    purpose: string;
    startDate: string;
    endDate: string;
    productId: number;
    productName: string;
    productLabel: string;
    rateAmount: number;
    currencyId: number;
    currencyCode: string;
    currencySymbol: string;
    convertedRateAmount: number;
    convertedCurrencyCode: string;
    convertedCurrencyId: number;
    travelerTypeId: number;
    typeName: string;
    isMultiTrip: boolean;
    email: string;
    mbl: string;
    createdAt: string;
    [key: string]: unknown;
  };
}

interface UseVerifyTokenReturn {
  verifyToken: (token: string, id?: string) => Promise<VerifyTokenResponse>;
  loading: boolean;
  error: string | null;
  success: boolean;
  clearState: () => void;
}

export const useVerifyToken = (): UseVerifyTokenReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { setPaymentPrice, setPaymentConvertedPrice, setAdjustmentPriceByAge, setTempDataId } = useAppStore();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const verifyToken = useCallback(async (token: string, id?: string): Promise<VerifyTokenResponse> => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // CRITICAL FIX: Minimal token validation - only check for empty tokens
      if (!token || !token.trim()) {
        throw new Error('Token is required');
      }

      const trimmedToken = token.trim();

      const requestBody: VerifyTokenRequest = {
        token: trimmedToken,
        ...(id && { id: id.trim() })
      };

      console.log('Verifying token:', {
        token: trimmedToken.substring(0, 8) + '...',
        tokenLength: trimmedToken.length,
        id: id || 'not provided',
        timestamp: new Date().toISOString()
      });

      const utmQsParams = getUtmQsParams(queryParams);

      // CRITICAL FIX: Use verificationApi's built-in timeout (15 seconds) instead of custom timeout
      // Make API call to verify token
      const response = await verificationApi.post('/api/verification/verify' + utmQsParams, requestBody);

      const data: VerifyTokenResponse = response.data;

      if (data.success) {
        if (data.tempData) {
            if(data.tempDataId){
              setTempDataId(data.tempDataId)
            }
            setPaymentConvertedPrice({
              currencyCode: data.tempData.convertedCurrencyCode!,
              rateAmount: data.tempData.convertedRateAmount!,
              currencyId: data.tempData.convertedCurrencyId!,
            });
            setPaymentPrice({
              currencyCode: data.tempData.currencyCode,
              rateAmount: data.tempData.rateAmount,
              currencyId: data.tempData.currencyId,
            });
            setAdjustmentPriceByAge(0)
          }
        setSuccess(true);
        return data;
      } else {
        throw new Error(data.message || 'Token verification failed');
      }

    } catch (err) {
      console.error('Token verification error:', err);
      
      let errorMessage = 'Token verification failed';
      
      if (axios.isAxiosError(err)) {
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          errorMessage = 'Request timeout: The verification server is taking too long to respond. Please try again.';
        } else if (err.response) {
          // Server responded with error status
          const serverMessage = err.response.data?.message || err.response.statusText;
          errorMessage = `Server error (${err.response.status}): ${serverMessage}`;
          
          // Handle specific error cases
          if (err.response.status === 400) {
            errorMessage = 'Invalid token format';
          } else if (err.response.status === 401) {
            errorMessage = 'Token is invalid or expired';
          } else if (err.response.status === 404) {
            errorMessage = 'Token not found or already used';
          } else if (err.response.status === 429) {
            errorMessage = 'Too many verification attempts. Please wait a moment and try again.';
          }
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Unable to connect to verification server. Please check your internet connection.';
        } else {
          // Something else happened
          errorMessage = `Request error: ${err.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Return error response
      return {
        success: false,
        code: axios.isAxiosError(err) && err.response?.status ? err.response.status : 500,
        message: errorMessage
      };

    } finally {
      setLoading(false);
    }
  }, []);

  // Clear all state
  const clearState = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    verifyToken,
    loading,
    error,
    success,
    clearState
  };
};